// Shared display helpers for the trading journal UI.

import type {
  JournalStrategy,
  JournalOptionStatus,
  JournalStockStatus,
  JournalAssignmentDecision,
  JournalTagCategory,
  JournalReview,
} from '@/models';

// Shared form-field styles (append `w-full` / `disabled:opacity-60` at the call site as needed).
export const JOURNAL_FIELD_CLASS = 'px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm';
export const JOURNAL_LABEL_CLASS = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

export const JOURNAL_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;

/** Text colour that stays readable on a user-picked badge colour. */
export function readableTextColor(hex: string): string {
  if (!JOURNAL_COLOR_REGEX.test(hex)) return '#ffffff';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62 ? '#111827' : '#ffffff';
}

export const STRATEGY_LABELS: Record<JournalStrategy, string> = {
  KERNSTRATEGIE: 'Kernstrategie',
  REPARATURSTRATEGIE: 'Reparatur',
  EARNING_TRADE: 'Earnings',
  UNKNOWN: 'Unbekannt',
};

export const OPTION_STATUS_LABELS: Record<JournalOptionStatus, string> = {
  OPEN: 'Offen',
  CLOSED: 'Geschlossen',
  ASSIGNED: 'Zugeteilt',
  EXPIRED: 'Verfallen',
  ROLLED: 'Gerollt',
};

export const STOCK_STATUS_LABELS: Record<JournalStockStatus, string> = {
  OPEN: 'Offen',
  CLOSED: 'Geschlossen',
};

export function optionStatusBadgeClass(status: JournalOptionStatus): string {
  switch (status) {
    case 'OPEN':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200';
    case 'CLOSED':
      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    case 'ASSIGNED':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200';
    case 'EXPIRED':
      return 'bg-lime-100 text-lime-800 dark:bg-lime-900/50 dark:text-lime-200';
    case 'ROLLED':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200';
  }
}

export function pnlColorClass(pnl: number | null): string {
  if (pnl === null) return 'text-gray-500 dark:text-gray-400';
  if (pnl > 0) return 'text-green-600 dark:text-green-400';
  if (pnl < 0) return 'text-red-600 dark:text-red-400';
  return 'text-gray-600 dark:text-gray-300';
}

export const ASSIGNMENT_DECISION_LABELS: Record<JournalAssignmentDecision, string> = {
  // Not "Offen" - that is already the label of the neighbouring position status.
  PENDING: 'Ausstehend',
  HOLD: 'Halten',
  COVERED_CALL: 'Covered Call',
  SELL: 'Verkaufen',
  CLOSED: 'Geschlossen',
};

export const ASSIGNMENT_DECISION_OPTIONS: JournalAssignmentDecision[] = ['PENDING', 'HOLD', 'COVERED_CALL', 'SELL', 'CLOSED'];

export const TAG_CATEGORY_LABELS: Record<JournalTagCategory, string> = {
  GENERAL: 'Allgemein',
  MISTAKE: 'Fehler',
  RULE: 'Regel',
};

export const TAG_CATEGORY_OPTIONS: JournalTagCategory[] = ['GENERAL', 'MISTAKE', 'RULE'];

export function tagCategoryBadgeClass(category: JournalTagCategory): string {
  switch (category) {
    case 'GENERAL':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200';
    case 'MISTAKE':
      return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200';
    case 'RULE':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200';
  }
}

export function reviewStatusBadgeClass(review: JournalReview, today: string): string {
  if (review.status === 'DONE') return 'bg-lime-100 text-lime-800 dark:bg-lime-900/50 dark:text-lime-200';
  if (review.status === 'SKIPPED') return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  if (review.scheduledFor <= today) return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200';
  return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200';
}

export function reviewStatusLabel(review: JournalReview, today: string): string {
  if (review.status === 'DONE') return 'Erledigt';
  if (review.status === 'SKIPPED') return 'Übersprungen';
  if (review.scheduledFor <= today) return 'Fällig';
  return 'Geplant';
}
